import { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DialogContainer from '@shared/ui/dialog/DialogContainer';
import { container } from '@infrastructure/container';
import { AmazonRegion } from '@domain/connection/IMarketplaceRepository';
import {
    buildAmazonConfirmUrl,
    disableReferrer,
    isAmazonConfirmUrl,
    readAmazonOAuthIntent,
    saveAmazonOAuthIntent,
} from '../amazonOAuth';

const REGIONS: { value: AmazonRegion; label: string }[] = [
    { value: 'eu', label: 'Europe' },
    { value: 'na', label: 'North America' },
    { value: 'fe', label: 'Far East' },
];

/**
 * Amazon's log-in URI. Seller Central sends the seller here so we can tie the
 * consent to the signed-in Juststock company, then continue to Amazon's confirm URL.
 */
const AmazonLogin = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const started = useRef(false);
    const [error, setError] = useState<string | null>(null);
    const [region, setRegion] = useState<AmazonRegion>(readAmazonOAuthIntent()?.region ?? 'eu');
    const [needsRegion, setNeedsRegion] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const amazonCallbackUri = searchParams.get('amazon_callback_uri');
    const amazonState = searchParams.get('amazon_state');
    const version = searchParams.get('version');

    const continueAuthorization = async (selectedRegion: AmazonRegion) => {
        if (!amazonCallbackUri || !amazonState || !isAmazonConfirmUrl(amazonCallbackUri)) {
            setError('Amazon sent an unexpected authorization link.');
            setIsLoading(false);
            return;
        }

        const intent = readAmazonOAuthIntent();
        const result = await container.amazonConnectorRepository.startAuthorization(selectedRegion, intent?.connectionId);
        if (result.status === 'error') {
            setError(result.error.message ?? 'Failed to continue Amazon sign-in');
            setIsLoading(false);
            return;
        }

        saveAmazonOAuthIntent({
            region: selectedRegion,
            connectionId: intent?.connectionId,
            returnPath: intent?.returnPath || `${window.location.pathname}`,
        });
        disableReferrer();
        window.location.href = buildAmazonConfirmUrl(
            amazonCallbackUri,
            amazonState,
            result.data.state,
            result.data.redirectUri,
            version,
        );
    };

    useEffect(() => {
        if (started.current) return;
        started.current = true;

        const intent = readAmazonOAuthIntent();
        if (!amazonCallbackUri || !amazonState) {
            setError('Missing Amazon authorization details.');
            setIsLoading(false);
            return;
        }
        if (!intent) {
            setNeedsRegion(true);
            setIsLoading(false);
            return;
        }
        continueAuthorization(intent.region).catch((err: unknown) => {
            setError(err instanceof Error ? err.message : 'Failed to continue Amazon sign-in');
            setIsLoading(false);
        });
    }, [amazonCallbackUri, amazonState]);

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
            <DialogContainer maxWidth="600px">
                <Box sx={{ margin: 4 }}>
                    <img src="/assets/images/ic_just_stock.png" alt="JustStock" style={{ height: 16 }} />
                    {isLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {needsRegion && !error && (
                        <Stack spacing={2} sx={{ pt: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                                Choose the Seller Central region for this account to continue.
                            </Typography>
                            <TextField select label="Seller region" value={region} onChange={(event) => setRegion(event.target.value as AmazonRegion)}>
                                {REGIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                ))}
                            </TextField>
                            <Button variant="contained" onClick={() => { setIsLoading(true); continueAuthorization(region); }}>
                                Continue to Amazon
                            </Button>
                        </Stack>
                    )}
                    {error && (
                        <Stack spacing={2} sx={{ pt: 3 }}>
                            <Typography color="error.main">{error}</Typography>
                            <Button variant="outlined" onClick={() => navigate('/')}>Back</Button>
                        </Stack>
                    )}
                </Box>
            </DialogContainer>
        </Box>
    );
};

export default AmazonLogin;
